import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowUpRight } from "lucide-react";
import { ARTICLES, SITE_URL } from "../../articles";
import { fadeUp } from "../../data";
import { useSeo } from "../../lib/useSeo";

export const ArticlesPage = () => {
  useSeo({
    title: "Articles & News | Puttraj",
    description: "Latest articles, career guidance and public-service updates from the Office of Puttraj.",
    canonical: `${SITE_URL}/articles`,
    url: `${SITE_URL}/articles`,
    image: `${SITE_URL}/photos/portrait.jpg`,
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": `${SITE_URL}/articles#page`,
          name: "Articles & News",
          url: `${SITE_URL}/articles`,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Articles", item: `${SITE_URL}/articles` },
          ],
        },
      ],
    },
  });

  return (
    <section className="pt-32 pb-24 md:pt-40 md:pb-32" style={{ background: "#FFFFFF" }}>
      <div className="container-x">
        <motion.div variants={fadeUp} initial="hidden" animate="show" className="max-w-3xl">
          <span className="eyebrow" style={{ color: "var(--blue)" }}>Articles &amp; News</span>
          <h1 className="font-display font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl mt-4" style={{ color: "var(--navy)" }}>
            Latest Articles
          </h1>
          <p className="font-body text-base md:text-lg leading-relaxed mt-6" style={{ color: "var(--text2)" }}>
            Career guidance, community notes and public-service perspectives from the Office of Puttraj.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-14">
          {ARTICLES.map((a, i) => (
            <motion.article key={a.slug} variants={fadeUp} custom={i} initial="hidden" animate="show"
              data-testid={`article-card-${a.slug}`}
              className="group rounded-2xl overflow-hidden border bg-white hover:-translate-y-1.5 hover:shadow-[0_18px_40px_rgba(25,118,210,0.12)] transition-all duration-300 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
              style={{ borderColor: "var(--grey)" }}>
              <Link to={`/articles/${a.slug}`} aria-label={a.title}>
                <div className="aspect-[16/9] overflow-hidden bg-[var(--softblue)]">
                  <img src={a.image} alt={a.imageAlt} loading="lazy" decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-body text-[11px] font-semibold tracking-wider uppercase px-3 py-1 rounded-full"
                      style={{ background: "rgba(21,87,214,0.10)", color: "var(--blue)" }}>{a.category}</span>
                  </div>
                  <h2 className="font-display font-semibold text-xl mt-4 leading-snug" style={{ color: "var(--navy)" }}>
                    {a.title}
                  </h2>
                  <p className="font-body text-sm leading-relaxed mt-3" style={{ color: "var(--text2)" }}>{a.excerpt}</p>
                  <div className="flex items-center justify-between mt-6 font-body text-xs" style={{ color: "var(--muted)" }}>
                    <span className="inline-flex items-center gap-4">
                      <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> {a.publishDateDisplay}</span>
                      <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {a.readMinutes} min read</span>
                    </span>
                    <span className="inline-flex items-center gap-1 font-semibold" style={{ color: "var(--blue)" }}>
                      Read <ArrowUpRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
